import React, { useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  Divider,
  InputAdornment,
  Grid,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { getOptimalParametersForTaskCount } from '../../utils/parameterUtils';

interface ParameterPanelProps {
  initialValues?: {
    taskCount?: number;
    qosProperties?: number;
    iterations?: number;
    initialPopulation?: number;
    selectionRate?: number;
    crossoverRate?: number;
    mutationRate?: number;
    memeplexCount?: number;
    numberOfRuns?: number;
  };
  onParametersChange?: (params: {
    taskCount: number;
    qosProperties: number;
    iterations: number;
    initialPopulation: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
    memeplexCount: number;
    numberOfRuns?: number;
  }) => void;
}

const ParameterPanel: React.FC<ParameterPanelProps> = ({ initialValues, onParametersChange }) => {
  // Initialize with optimal parameters based on taskCount if no initialValues provided
  const initialTaskCount = initialValues?.taskCount ?? 10;
  const optimalParams = getOptimalParametersForTaskCount(initialTaskCount);

  const [parameters, setParameters] = useState({
    taskCount: initialTaskCount,
    qosProperties: initialValues?.qosProperties ?? 9,
    iterations: initialValues?.iterations ?? optimalParams.iterations,
    initialPopulation: initialValues?.initialPopulation ?? optimalParams.initialPopulation,
    selectionRate: initialValues?.selectionRate ?? 10,
    crossoverRate: initialValues?.crossoverRate ?? optimalParams.crossoverRate,
    mutationRate: initialValues?.mutationRate ?? optimalParams.mutationRate,
    memeplexCount: initialValues?.memeplexCount ?? optimalParams.memeplexCount,
    numberOfRuns: initialValues?.numberOfRuns ?? 1,
  });

  // Update parameters when initialValues change (for preset loading and complete run updates)
  React.useEffect(() => {
    if (initialValues) {
      // Use all provided values - the context already calculates optimal parameters
      // Only update if values actually changed to avoid unnecessary re-renders
      setParameters(prev => {
        // Check if taskCount changed - if so, use all new values (they include optimal params)
        if (initialValues.taskCount !== undefined && initialValues.taskCount !== prev.taskCount) {
          return {
            taskCount: initialValues.taskCount,
            qosProperties: initialValues.qosProperties ?? prev.qosProperties,
            iterations: initialValues.iterations ?? prev.iterations,
            initialPopulation: initialValues.initialPopulation ?? prev.initialPopulation,
            selectionRate: initialValues.selectionRate ?? prev.selectionRate,
            crossoverRate: initialValues.crossoverRate ?? prev.crossoverRate,
            mutationRate: initialValues.mutationRate ?? prev.mutationRate,
            memeplexCount: initialValues.memeplexCount ?? prev.memeplexCount,
            numberOfRuns: initialValues.numberOfRuns ?? prev.numberOfRuns,
          };
        }
        // Otherwise, merge with existing values
        return {
          ...prev,
          ...initialValues,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.taskCount, initialValues?.iterations, initialValues?.initialPopulation, 
      initialValues?.crossoverRate, initialValues?.mutationRate, initialValues?.memeplexCount]);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value, 10) || 0;
    let newParams = { ...parameters, [field]: value };
    
    // If taskCount changes, auto-adjust other parameters based on problem size
    if (field === 'taskCount') {
      const optimal = getOptimalParametersForTaskCount(value);
      newParams = {
        ...newParams,
        ...optimal,
      };
    }
    
    setParameters(newParams);
    if (onParametersChange) {
      onParametersChange(newParams);
    }
  };

  // Initial notification
  React.useEffect(() => {
    if (onParametersChange) {
      onParametersChange(parameters);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2">
          Parameters
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Common Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Common Parameters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <TextField
            label="Workflow Tasks"
            type="number"
            value={parameters.taskCount}
            onChange={handleChange('taskCount')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 1000 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="QoS Properties"
            type="number"
            value={parameters.qosProperties}
            onChange={handleChange('qosProperties')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 20 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Iterations"
            type="number"
            value={parameters.iterations}
            onChange={handleChange('iterations')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 10000 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Initial Population"
            type="number"
            value={parameters.initialPopulation}
            onChange={handleChange('initialPopulation')}
            size="small"
            fullWidth
            inputProps={{ min: 10, max: 10000 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* GA Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Genetic Algorithm (GA)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <TextField
            label="Selection Rate"
            type="number"
            value={parameters.selectionRate}
            onChange={handleChange('selectionRate')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Crossover Rate"
            type="number"
            value={parameters.crossoverRate}
            onChange={handleChange('crossoverRate')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Mutation Rate"
            type="number"
            value={parameters.mutationRate}
            onChange={handleChange('mutationRate')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* SFLA Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        SFLA Parameters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <TextField
            label="Memeplex Count"
            type="number"
            value={parameters.memeplexCount}
            onChange={handleChange('memeplexCount')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 100 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Statistical Analysis Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
        Statistical Analysis
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Number of Runs"
            type="number"
            value={parameters.numberOfRuns}
            onChange={handleChange('numberOfRuns')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 100 }}
            helperText="Multiple runs for statistical analysis"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ParameterPanel;

